import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Accordion,
  arrayUtils,
  Headline,
  Label,
  responsiveUtils,
  TextBlock,
  timeUtils,
} from '@sato/components';
import { observer } from 'mobx-react-lite';
import * as yup from 'yup';

import { IWidget, ParsedEntry } from '@/contentful/types';

import { api } from '@api';
import { HookFormDatePicker } from '@components/ReactHookFormFields/HookFormDatePicker';
import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { HookFormTextInput } from '@components/ReactHookFormFields/HookFormTextInput';
import { BODY_MAX_LENGTH, TOPIC_MAX_LENGTH } from '@constants/index';
import { useIncidentCreation } from '@hooks/incidents';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { useParkingContract } from '@hooks/useParkingContract';
import { useParkingProduct } from '@hooks/useParkingProduct';
import { ParkingSpaceIncidentRequest } from '@models/api';
import { SerializedPlainDate } from '@models/api/data.types';
import {
  IncidentClassification,
  IncidentSubClassification,
} from '@models/Incident';
import { QUERY_STRINGS } from '@routes/routes.types';
import { apartmentStore } from '@stores/apartmentStore';
import { localizationStore, t } from '@stores/localization';
import { ParkingCardFromParkingContract } from '@views/parking/components/ParkingCardFromParkingContract';
import { ParkingProductCard } from '@views/parking/components/ParkingProductCard';
import { ParkingSpacePaymentsList } from '@views/parking/pages/ParkingSpaceRentingPage/components/ParkingSpacePaymentsList';
import { ParkingSpaceSpecificationsList } from '@views/parking/pages/ParkingSpaceRentingPage/components/ParkingSpaceSpecificationsList';
import {
  getParkingProductAvailableDate,
  getParkingSpaceStartDateSchema,
  getPossibleParkingStartDateRange,
} from '@views/parking/parking.utils';

import { IncidentFormWrapper } from '../IncidentFormWrapper';

type ParkingRentingFormProps = {
  title: string;
  description: string;
  files?: File[];
  startDate?: SerializedPlainDate;
};

export const ParkingRentingQuestionForm = observer(
  ({ customTranslations }: ParsedEntry<IWidget>) => {
    const [searchParams] = useSearchParams();

    const { t: incidentFormT, tContentful } =
      useCustomTranslations(customTranslations);

    /** The id of the product the user is inquiring about, be it renting or switching to. */
    const inquiredParkingProductId = searchParams.get(
      QUERY_STRINGS.PARKING_PRODUCT_ID
    );

    /**  Id of users current parking contract that they want to switch from to `inquiredParkingProductId`. */
    const currentParkingContractId = searchParams.get(
      QUERY_STRINGS.PARKING_CONTRACT_ID
    );

    const { data: inquiredParkingProduct } = useParkingProduct(
      inquiredParkingProductId,
      localizationStore.language
    );

    const { parkingContract: currentParkingContract } = useParkingContract(
      currentParkingContractId
    );

    const isSwitchingParkingSpaces =
      !!currentParkingContract && !!inquiredParkingProduct;

    const isRentingNewSpace =
      !isSwitchingParkingSpaces && !!inquiredParkingProduct;

    const isLinkedParkingArea =
      !!apartmentStore.selectedApartment?.realestate.linkedParkingAreas?.some(
        (area) => area.id === inquiredParkingProduct?.parkingArea.id
      );

    const laterStartDate = getParkingProductAvailableDate(
      inquiredParkingProduct
    );

    const {
      firstPossibleStartDate,
      lastPossibleStartDate,
      isOnlyOnePossibleStartDate,
    } = getPossibleParkingStartDateRange(laterStartDate);

    const getInitialStartDate = (): string | undefined => {
      if (!inquiredParkingProduct) return undefined;

      if (isOnlyOnePossibleStartDate) {
        return timeUtils.formatISO(firstPossibleStartDate, {
          representation: 'date',
        });
      }

      return '';
    };

    const classifications = {
      classification: IncidentClassification.FACILITY_RESERVATION,
      subClassification: IncidentSubClassification.PARKING_SPACE_RENTING,
    };

    const postIncident = ({
      startDate,
      ...formValues
    }: ParkingSpaceIncidentRequest) =>
      api.incidents.postGenericIncident({
        ...formValues,
        parkingSpaceId: inquiredParkingProduct?.parkingSpace.id,
        description: arrayUtils.joinArrayString(
          [
            startDate && `${t('parking.startDatePrefix')} ${startDate}`,
            formValues.description,

            isSwitchingParkingSpaces &&
              `${t('parking.current_parking_space')}: ${currentParkingContract?.parkingSpace?.name}.` +
                '\n' +
                `${t('parking.interestedInChanging')}: ${inquiredParkingProduct.name}.`,

            isRentingNewSpace &&
              `${t('parking.interestedInRenting')}: ${inquiredParkingProduct.name}`,
            isLinkedParkingArea && t('parking.linkedParkingDescriptionText'),
          ],
          '\n\n'
        ),
      });

    const { create } = useIncidentCreation({
      postIncident,
      classifications,
      messageTitlePrefix: incidentFormT('messageTitlePrefix'),
      successDialogBody: t('parking.renting_message_sent_body'),
    });

    return (
      <>
        {inquiredParkingProduct && (
          <section className="grid gap-x-3 gap-y-4 lg:grid-cols-[1fr_1fr]">
            <div>
              <Headline level={2} className="py-4">
                {isSwitchingParkingSpaces
                  ? `${t('parking.parking_space_changers_title')} ${inquiredParkingProduct.name}`
                  : t('parking.interestedInRenting')}
              </Headline>

              {isSwitchingParkingSpaces && (
                <TextBlock className="mb-5">
                  {t('parking.parkingSpaceChangersDescription')}
                </TextBlock>
              )}

              <ParkingProductCard
                parkingProduct={inquiredParkingProduct}
                className="max-w-md !shadow-sm"
              />

              {currentParkingContract && (
                <div className="my-5 lg:mt-7">
                  <Label component="h3" className="py-2">
                    {t('parking.your_current_parking_space')}
                  </Label>

                  <ParkingCardFromParkingContract
                    parkingContract={currentParkingContract}
                    className="max-w-md"
                  />
                </div>
              )}
            </div>

            {inquiredParkingProduct && (
              <Accordion
                title={t('parking.parking_space_specifications')}
                defaultExpanded={responsiveUtils.screenSizeExceedsBreakpoint.lg}
                wrapperClasses="h-min data-[expanded]:border-none"
              >
                <section className="lg:mt-5">
                  <ParkingSpaceSpecificationsList
                    parkingSpace={inquiredParkingProduct.parkingSpace}
                    parkingArea={inquiredParkingProduct.parkingArea}
                  />
                </section>

                <section className="mt-5">
                  <Headline level={3} component="h2" className="mb-4 lg:mb-6">
                    {t('parking.space_payments')}
                  </Headline>

                  <ParkingSpacePaymentsList
                    parkingSpace={inquiredParkingProduct.parkingSpace}
                    parkingArea={inquiredParkingProduct.parkingArea}
                    rent={inquiredParkingProduct.rent}
                  />
                </section>
              </Accordion>
            )}
          </section>
        )}

        <IncidentFormWrapper<ParkingRentingFormProps>
          incidentKind="ParkingRentingQuestion"
          withFileUpload
          values={{
            title: isSwitchingParkingSpaces
              ? `${t('parking.parking_space_changers_title')} ${inquiredParkingProduct.name}`
              : '',
            description: '',
            files: undefined,
            startDate: getInitialStartDate(),
          }}
          validationSchema={yup.object().shape({
            startDate: inquiredParkingProduct
              ? getParkingSpaceStartDateSchema(laterStartDate, t)
              : yup.string().optional(),
            title: yup.string().required(),
            description: yup.string().required(),
            files: yup.array().files(tContentful),
          })}
          onSubmit={create}
          className="border-t-0"
        >
          {inquiredParkingProduct && (
            <>
              <Label className="text-2">
                {incidentFormT('startDateHeading')}
              </Label>

              <HookFormDatePicker
                label={incidentFormT('startDateLabel')}
                name="startDate"
                minValue={firstPossibleStartDate}
                maxValue={lastPossibleStartDate}
                isReadOnly={isOnlyOnePossibleStartDate}
                isRequired
              />
            </>
          )}
          <HookFormTextInput
            type="text"
            name="title"
            label={{ value: incidentFormT('titleLabel'), type: 'static' }}
            placeholder={incidentFormT('titlePlaceholder')}
            maxLength={TOPIC_MAX_LENGTH}
            isDisabled={isSwitchingParkingSpaces}
          />

          <HookFormTextArea
            type="text"
            label={{ value: incidentFormT('descriptionLabel'), type: 'static' }}
            name="description"
            placeholder={incidentFormT('descriptionPlaceholder')}
            message={incidentFormT('descriptionHelper')}
            rows={6}
            maxLength={BODY_MAX_LENGTH}
          />
        </IncidentFormWrapper>
      </>
    );
  }
);
